import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import classNameBind from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import Header from '../Header';
import Label from '../Label';
import Text from '../Text';
import { TextStyle } from '../TextStyle';
import Spinner from '../Spinner';
import Icon from '../Icon';
import uuid from '../../utils/methods/uuid';
import styles from './List.module.css';
import ListItem from './components/ListItem';
import ExpandableWrapper from './components/ExpandableWrapper';
import ExpandableWrapperWithColumns from './components/ExpandableWrapperWithColumns';
import SortableHeader from './components/SortableHeader';

const bindClasses = classNameBind.bind(styles);

const passPropsToChildren = (children, columns, isLoading) => (
  React.Children.map(children, child => (
    React.cloneElement(child, { columns, className: bindClasses('ListItem'), isLoading })
  ))
);

const renderEmptyElements = (emptyListText, emptyListContent) => (
  <div
    className={bindClasses('empty')}
    data-ppuc-id="List.zeroState"
  >
    <Text type="subdued">
      {emptyListText}
    </Text>
    {typeof emptyListContent !== 'undefined' && (
      <div className={bindClasses('empty-list-content')}>
        {emptyListContent}
      </div>
    )}
  </div>
);

const renderErrorState = t => (
  <div
    className={bindClasses('empty', 'withError')}
    data-ppuc-id="List.withError"
  >
    <div className={bindClasses('errorWrapper')}>
      <Icon name="exclamation-mark-circle" color="red" />
      <TextStyle type="negative">
        {t('listErrorMsg')}
      </TextStyle>
    </div>
  </div>
);

const renderContent = (children, columns, error, emptyListText, emptyListContent, t, isLoading) => {
  if (error) {
    return renderErrorState(t);
  }
  if (children && !_isEmpty(children)) {
    return passPropsToChildren(children, columns, isLoading);
  }

  return renderEmptyElements(emptyListText || t('thereAreNoItems'), emptyListContent);
};

const List = ({
  title,
  columns,
  error,
  children,
  emptyListText,
  emptyListContent,
  className,
  isLoading,
  headerColor,
  noBorderHeader,
}) => {
  const { t } = useTranslation('components');
  const hasColumnName = columns.some(column => typeof column.name !== 'undefined');

  return (
    <div
      className={bindClasses('wrapper', { isLoading }, className)}
      data-ppuc-id="List"
    >
      {title && (
        <div className={bindClasses('title')}>
          <Header>{title}</Header>
        </div>
      )}
      {hasColumnName && (
        <div
          className={bindClasses('header', { noBorderHeader })}
          data-ppuc-id="List.header"
        >
          {columns.map(column => (
            <div
              className={bindClasses(
                'column-name',
                `align-content-${column.align}`,
                `flex-${column.size}`,
                `header-${headerColor}`,
                { withSortableButton: column.name?.type?.displayName === 'List.SortableHeader' },
              )}
              key={typeof column.key !== 'undefined' ? column.key : uuid()}
            >
              {column.name && (
                typeof column.name === 'string' ? <Label type="small">{column.name}</Label> : column.name
              )}
            </div>
          ))}
        </div>
      )}
      <div className={bindClasses('content')}>
        {isLoading && (
          <div className={bindClasses('spinner-wrapper')}>
            <Spinner type="centered" />
          </div>
        )}
        {renderContent(children, columns, error, emptyListText, emptyListContent, t, isLoading)}
      </div>
    </div>
  );
};

List.defaultProps = {
  title: '',
  columns: [],
  error: false,
  children: null,
  className: '',
  emptyListText: '',
  isLoading: false,
  headerColor: 'transparent',
};

List.Item = ListItem;
List.ExpandableWrapper = ExpandableWrapper;
List.ExpandableWrapperWithColumns = ExpandableWrapperWithColumns;
List.SortableHeader = SortableHeader;

export default List;
